import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminGallery({ credentials, userRole }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  const canModify = userRole === 'admin';
  const [uploadForm, setUploadForm] = useState({ title: "", description: "" });

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Failed to load gallery images");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", uploadForm.title);
    formData.append("description", uploadForm.description);

    try {
      await axios.post(`${API}/gallery`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Image uploaded successfully");
      setUploadForm({ title: "", description: "" });
      fetchImages();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/gallery/${deleteId}`, { headers });
      toast.success("Image deleted");
      setDeleteId(null);
      fetchImages();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex justify-between items-center mb-8">
        <h1 className="heading-lg">Gallery</h1>
      </div>

      {/* Upload Section */}
      <Card className="card-heritage mb-8">
        <CardHeader>
          <CardTitle className="font-serif flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#14532D]" />
            Upload New Photo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="label-heritage">Title (optional)</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                className="input-heritage"
                placeholder="Photo title"
                data-testid="gallery-title-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="label-heritage">Description (optional)</Label>
              <Input
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                className="input-heritage"
                placeholder="Photo description"
                data-testid="gallery-description-input"
              />
            </div>
          </div>
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#D6D3D1] rounded-lg cursor-pointer hover:border-[#14532D] transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {uploading ? (
                <div className="spinner" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-[#78716C] mb-2" />
                  <p className="text-sm text-[#78716C]">Click to upload photo</p>
                  <p className="text-xs text-[#A8A29E]">JPEG, PNG, GIF, WebP (max 10MB)</p>
                </>
              )}
            </div>
            <input 
              type="file" 
              className="hidden" 
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="gallery-file-input"
            />
          </label>
        </CardContent>
      </Card>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : images.length === 0 ? (
        <Card className="card-heritage">
          <CardContent className="p-12 text-center">
            <Images className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#78716C]">No gallery images yet</p>
            <p className="text-sm text-[#A8A29E] mt-2">Upload your first photo above</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {images.map((image) => (
            <Card key={image.id} className="card-heritage overflow-hidden" data-testid={`gallery-image-${image.id}`}>
              <div className="aspect-square bg-[#F5F5F0]">
                <img 
                  src={image.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` : image.url}
                  alt={image.title || "Gallery image"}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                {image.title && (
                  <h4 className="font-semibold text-sm text-[#1C1917] mb-1">{image.title}</h4>
                )}
                {image.description && (
                  <p className="text-xs text-[#78716C] mb-2 line-clamp-2">{image.description}</p>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#A8A29E]">
                    {new Date(image.created_at).toLocaleDateString('en-GB')}
                  </span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-[#B91C1C] h-8 px-2"
                    onClick={() => setDeleteId(image.id)}
                  >
                    <Trash2 size={14} className="mr-1" /> Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this photo from the gallery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteImage} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Ticket Bookings Component
